import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { InformationEntityComponent } from '../../../../shared/components/information-entity/information-entity.component';
import { DropdownModule } from 'primeng/dropdown';
import { TemplateDirective } from '../../../../shared/directives/template.directive';
import { AgentGroupsPipe } from '../../pipes/agent-groups.pipe';
import { FullNamePipe } from '../../../../shared/pipes/full-name.pipe';
import { Store } from '@ngrx/store';
import { AGENTS_API_ACTIONS } from '../../../../store/actions';
import { combineLatest, filter, firstValueFrom, Observable, switchMap } from 'rxjs';
import { AGENTS_REDUCER, IAgentsState } from '../../../../store/reducers/agents.reducer';
import { AsyncPipe } from '@angular/common';
import { CONVERSATION_DATA_REDUCER } from '../../../../store/reducers/conversation-data.reducer';
import { IApiState } from '../../../../shared/models/api-state.model';
import { IConversationDetails } from '../../../../shared/models/conversation-details.model';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { IAgent } from '../../../../shared/models/agents.model';
import { ScreenLoaderComponent } from '../../../../shared/components/loader/screen-loader.component';
import { FormState } from '../../../../core/classes/form-state';

const UNKNOWN_AGENT_ID = -2;

@Component({
  selector: 'cv-agent-field',
  standalone: true,
  templateUrl: './agent-field.component.html',
  styleUrl: './agent-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    InformationEntityComponent,
    DropdownModule,
    TemplateDirective,
    AgentGroupsPipe,
    FullNamePipe,
    AsyncPipe,
    ReactiveFormsModule,
    ScreenLoaderComponent,
  ],
})
export class AgentFieldComponent extends FormState<IAgentFieldForm> {
  private readonly conversationDetailsState$: Observable<IApiState<IConversationDetails>> = this.store.select(
    CONVERSATION_DATA_REDUCER.selectConversationDetails,
  );
  private readonly agentsState$: Observable<IAgentsState> = this.store.select(AGENTS_REDUCER.selectAgentsState);
  readonly data$: Observable<IAgentFieldComponentData> = combineLatest({
    agentsState: this.agentsState$,
    conversationDetails: this.conversationDetailsState$,
  });
  readonly UNKNOWN_AGENT_ID = UNKNOWN_AGENT_ID;
  isAgentEditMode: boolean = false;

  constructor(
    private readonly store: Store,
    private readonly fb: FormBuilder,
  ) {
    super();
    this.form = this.fb.group<IAgentFieldForm>({ agent: this.fb.control(null) });
  }

  async onSubmit(): Promise<void> {
    const hasUnsavedChanges = await firstValueFrom(this.hasUnsavedChanges$);
    const agent = this.form.value.agent;
    if (hasUnsavedChanges && agent) {
      this.store.dispatch(AGENTS_API_ACTIONS.update(agent));
      await firstValueFrom(this.agentsState$.pipe(filter(({ updating }) => !updating)));
      this.form.reset();
      this.isAgentEditMode = false;
    }
  }

  async editAgent(): Promise<void> {
    this.isAgentEditMode = true;
    const agent = await firstValueFrom(
      this.conversationDetailsState$.pipe(
        switchMap(({ data }) => this.store.select(AGENTS_REDUCER.getAgentByName(data))),
      ),
    );
    if (agent) this.form.patchValue({ agent });
  }
}

interface IAgentFieldComponentData {
  agentsState: IAgentsState;
  conversationDetails: IApiState<IConversationDetails>;
}

interface IAgentFieldForm {
  agent: FormControl<IAgent | null>;
}
